import { useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { STATIC_FIELD_TYPES, type FormSchema } from '@hcc/types';
import { api } from '../../lib/api';
import { FieldView } from './formfields/FieldView';
import type { FieldValue } from './formfields/formValues';

interface SubmissionResp {
  submitted: boolean;
  title?: string;
  schema?: FormSchema;
  values?: Record<string, FieldValue>;
  files?: Record<string, string[]>;
  submitted_at?: string | null;
}

/**
 * The inductee's own submitted answers, read-only. Renders against the
 * version schema they filled (labels + order match) using the shared
 * FieldView in disabled mode. Lets them check for mistakes; correcting is
 * HR's call (re-open), so there are no inputs here.
 */
export function MyFormSubmission({ taskId }: { taskId: string }) {
  const q = useQuery({
    queryKey: ['my-form-submission', taskId],
    queryFn: () =>
      api<SubmissionResp>(`/api/me/onboarding/forms/${taskId}/submission`),
  });

  if (q.isLoading) return <p className="text-sm text-fg-muted">Loading your answers…</p>;
  if (!q.data || !q.data.submitted || !q.data.schema) {
    return <p className="text-sm text-fg-muted">You haven't submitted this form yet.</p>;
  }

  const { schema, values = {}, files = {} } = q.data;

  return (
    <div className="space-y-5">
      {schema.pages.map((page, pi) => (
        <div key={pi} className="space-y-3">
          {page.title ? (
            <div className="text-sm font-semibold text-fg">{page.title}</div>
          ) : null}
          {page.fields
            .filter((f) => !STATIC_FIELD_TYPES.includes(f.type))
            .map((f) =>
              f.type === 'signature' ? (
                <div key={f.key} className="text-sm">
                  <div className="font-medium text-fg-muted">{f.label}</div>
                  <div className="mt-1 text-fg">
                    {(files[f.key] ?? []).length > 0 ? '✓ Signed' : 'Not signed'}
                  </div>
                </div>
              ) : f.type === 'file_upload' ? (
                <div key={f.key} className="text-sm">
                  <div className="font-medium text-fg-muted">{f.label}</div>
                  {(files[f.key] ?? []).length > 0 ? (
                    <ul className="mt-1 space-y-1">
                      {(files[f.key] ?? []).map((name, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-fg">
                          <Paperclip className="h-3.5 w-3.5 text-fg-dim" />
                          {name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-1 text-fg-dim">No files attached</div>
                  )}
                </div>
              ) : (
                <FieldView key={f.key} field={f} value={values[f.key] ?? null} onChange={() => {}} disabled />
              ),
            )}
        </div>
      ))}
      <p className="rounded-token-sm bg-surface-2 px-3 py-2 text-xs text-fg-muted">
        This is a read-only copy of what you submitted. If something needs changing,
        contact your onboarding contact and they can re-open the form for you.
      </p>
    </div>
  );
}
